// Shared timing helpers for Almanac (informational, third-party) events.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`. Pure and
// clock-injectable (`now` defaults to `Date.now()`) so it can be unit-tested
// without touching the system clock.

import type { InformationalEventTiming } from '@@/types/api';

/**
 * Whether an Almanac event has concluded (its end/start moment is in the past).
 *
 * Concluded only when a CONCRETE end/start datetime exists AND is before `now`.
 * Coarse/undated timing variants (month/season/year/tba → null `starts_at` and
 * `ends_at`) are NEVER treated as concluded, and no datetime is fabricated for
 * them. `ends_at` is preferred over `starts_at` as the reference point.
 *
 * @param timing The event's timing tagged union (nullable/undefined tolerated).
 * @param now Epoch ms to compare against; defaults to `Date.now()`.
 */
export function isInformationalEventConcluded(
  timing: InformationalEventTiming | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!timing) return false;
  const reference = timing.ends_at ?? timing.starts_at;
  if (!reference) return false;
  const when = new Date(reference).getTime();
  if (Number.isNaN(when)) return false;
  return when < now;
}
